import { useTranslations } from "adapters/i18n";
import { useMemo } from "react";
import type { AgeRange, WeightGainCurve } from "shared/types";
import { CategoryGender } from "../utils/category.utils";

type UseCategoryFormOptionsProps = {
  ageRanges: AgeRange[];
  selectedGender: CategoryGender;
  weightGainCurves: WeightGainCurve[];
};

export function useCategoryFormOptions({
  ageRanges,
  selectedGender,
  weightGainCurves,
}: UseCategoryFormOptionsProps) {
  const { t } = useTranslations();

  const genderOptions = useMemo<{ value: CategoryGender; label: string }[]>(
    () => [
      {
        value: CategoryGender.Male,
        label: t("common:gender-options.male-short"),
      },
      {
        value: CategoryGender.Female,
        label: t("common:gender-options.female-short"),
      },
    ],
    [t]
  );

  const ageRangeOptions = useMemo<{ value: number; label: string }[]>(
    () =>
      ageRanges
        .filter((item) => item.gender === selectedGender)
        .map((item) => ({
          value: item.id,
          label: item.ageRange,
        })),
    [ageRanges, selectedGender]
  );

  const weightGainCurveOptions = useMemo<
    { value: number | null; label: string }[]
  >(
    () =>
      weightGainCurves.length
        ? weightGainCurves.map((item) => ({
            value: item.id,
            label: item.name,
          }))
        : [
            {
              value: null,
              label: t("common:no-registrations"),
            },
          ],
    [t, weightGainCurves]
  );

  return {
    genderOptions,
    ageRangeOptions,
    weightGainCurveOptions,
  };
}
